import { ChevronRight } from "lucide-react";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";

/**
 * The ONE footer disclosure under a Lane V2 answer: "Sources read (N) · Queries (M) ▸".
 *
 * What the answer's tool calls actually were, as MSA wrote them from the turn's call log: the sources it read, and the data queries
 * it ran (the `## Data results` section, raw SOQL included, which used to sit in the answer body louder than the answer's own
 * headings). Collapsed by default; native `<details>` (keyboard and screen-reader behaviour for free, no state), like
 * `CompactionDivider`. Entries that are saved result files are `artifact://` links; the transcript's `ArtifactLinkContext`
 * renders each as the chip that opens the existing evidence/file view, so no second viewer lives here.
 *
 * An answer that read nothing renders NOTHING here: it is a muted tag in the completion row (`noSourcesLabel`), not a line of its own.
 */
export const NO_SOURCES_LABEL = "No org sources read";

/** The disclosure's one-line summary: `Sources read (N)`, `Queries (M)`, or both joined, omitting a part that is zero. */
export function sourcesSummary(count: number, queryCount: number): string {
  const parts: string[] = [];
  if (count > 0) parts.push(`Sources read (${count})`);
  if (queryCount > 0) parts.push(`Queries (${queryCount})`);
  return parts.join(" · ");
}

export function SourcesReadBlock({
  count,
  listMarkdown,
  queryCount = 0,
  queriesMarkdown = "",
}: {
  count: number;
  listMarkdown: string;
  queryCount?: number;
  queriesMarkdown?: string;
}) {
  if (count === 0 && queryCount === 0) return null;
  return (
    <details
      data-testid="sources-read"
      data-sources-count={count}
      data-queries-count={queryCount}
      className="group text-[12px] text-muted-foreground"
    >
      <summary className="inline-flex cursor-pointer select-none list-none items-center gap-1 [&::-webkit-details-marker]:hidden">
        <span>{sourcesSummary(count, queryCount)}</span>
        <ChevronRight aria-hidden className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90 motion-reduce:transition-none" />
      </summary>
      {listMarkdown && (
        <div className="mt-1.5">
          <SkillResultMarkdown content={listMarkdown} className="ma-answer ma-answer-quiet" />
        </div>
      )}
      {queriesMarkdown && (
        <div className="mt-1.5" data-testid="queries-read">
          {count > 0 && <div className="mb-0.5 text-[11px] font-medium uppercase tracking-[0.06em]">Queries</div>}
          <SkillResultMarkdown content={queriesMarkdown} className="ma-answer ma-answer-quiet" />
        </div>
      )}
    </details>
  );
}
